import { z } from 'zod';
import { api } from './client';
import type {
  IdpConfig,
  IdpTestResult,
  KubeconfigResponse,
  Paginated,
  Role,
  GroupRoleMapping,
} from './schemas';
import {
  AppSettings as AppSettingsSchema,
  type AppSettings,
  AuditEvent as AuditEventSchema,
  type AuditEvent,
  Binding as BindingSchema,
  DashboardStats as DashboardStatsSchema,
  Identity as IdentitySchema,
  IssuedToken as IssuedTokenSchema,
  KubeconfigResponse as KubeconfigResponseSchema,
  LoginResponse as LoginResponseSchema,
  Namespace as NamespaceSchema,
  Paginated as PaginatedSchema,
  RevokeResult as RevokeResultSchema,
  type Binding,
  type Identity,
  type IssuedToken,
  type LoginResponse,
  type Namespace,
  type RevokeResult,
} from './schemas';

export type UsersListParams = {
  q?: string;
  status?: string;
  namespace?: string;
  page?: number;
  pageSize?: number;
};

export type AuditListParams = {
  q?: string;
  actor?: string;
  action?: string;
  target?: string;
  namespace?: string;
  category?: string;
  result?: string;
  from?: string;
  to?: string;
  limit?: number;
  page?: number;
};

export const authApi = {
  me: () => api.get<Identity>('/auth/me', { skipRefreshOn401: true, schema: IdentitySchema }),
  login: (email: string, password: string, remember?: boolean) =>
    api.post<LoginResponse>('/auth/login', { email, password, remember }, { schema: LoginResponseSchema }),
  loginMfa: (mfaToken: string, otp: string) =>
    api.post<LoginResponse>('/auth/login/mfa', { mfaToken, otp }, { schema: LoginResponseSchema }),
  // Forced first-login MFA enrolment (gated by the enroll token from login).
  loginMfaEnroll: (enrollToken: string) =>
    api.post<{ otpauthUrl: string; secret: string; qrPng: string; enrollToken: string }>('/auth/login/mfa-enroll', { enrollToken }),
  loginMfaVerify: (enrollToken: string, otp: string) =>
    api.post<LoginResponse>('/auth/login/mfa-verify', { enrollToken, otp }, { schema: LoginResponseSchema }),
  logout: () => api.post<void>('/auth/logout'),
  refresh: () => api.post<void>('/auth/refresh'),
  oidcCallback: (search: string) => api.get<{ status: 'ok' } | LoginResponse>(`/auth/oidc/callback${search}`),
  // MFA self-service. Enroll returns the TOTP secret + otpauth URI (camelCased
  // from the wire otpauth_url); verify turns MFA on; disable turns it off.
  mfaEnroll: () => api.post<{ otpauthUrl: string; secret: string; qrPng: string }>('/auth/mfa/enroll'),
  mfaVerify: (otp: string) => api.post<void>('/auth/mfa/verify', { otp }),
  mfaDisable: () => api.post<void>('/auth/mfa/disable'),
  changePassword: (oldPw: string, newPw: string) => api.post<void>('/auth/password', { old: oldPw, new: newPw }),
};

/** What the credentials form can ask for; anything omitted keeps the server default. */
export type KubeconfigOptions = {
  ttlSeconds?: number;
  clusterName?: string;
  userName?: string;
  contextName?: string;
};

// Only send what was actually chosen: an empty field means "server default",
// and sending "" would be rejected as an invalid name.
function kubeconfigBody(opts: KubeconfigOptions): Record<string, unknown> | undefined {
  const body: Record<string, unknown> = {};
  if (opts.ttlSeconds && opts.ttlSeconds > 0) body.ttlSeconds = opts.ttlSeconds;
  if (opts.clusterName?.trim()) body.clusterName = opts.clusterName.trim();
  if (opts.userName?.trim()) body.userName = opts.userName.trim();
  if (opts.contextName?.trim()) body.contextName = opts.contextName.trim();
  return Object.keys(body).length > 0 ? body : undefined;
}

export const usersApi = {
  list: (params: UsersListParams = {}) =>
    api.get<Paginated<Identity>>('/users', {
      params: params as Record<string, string | number | boolean | undefined>,
      // readonly users get a bare array (just themselves); everyone else a page.
      schema: z.union([PaginatedSchema(IdentitySchema), z.array(IdentitySchema)]),
    }),
  get: (id: string) => api.get<Identity>(`/users/${encodeURIComponent(id)}`, { schema: IdentitySchema }),
  create: (data: Partial<Identity> & { password?: string; accountType?: string }) => api.post<Identity>('/users', data),
  update: (id: string, data: Partial<Identity>) => api.patch<Identity>(`/users/${encodeURIComponent(id)}`, data),
  remove: (id: string) => api.del<void>(`/users/${encodeURIComponent(id)}`),
  lock: (id: string) => api.post<void>(`/users/${encodeURIComponent(id)}/lock`),
  kubeconfig: (id: string, opts: KubeconfigOptions = {}) =>
    api.post<KubeconfigResponse>(
      `/users/${encodeURIComponent(id)}/kubeconfig`,
      kubeconfigBody(opts),
      { schema: KubeconfigResponseSchema },
    ),
  rotateToken: (id: string) => api.post<{ ok: boolean }>(`/users/${encodeURIComponent(id)}/rotate-token`),
  tokens: (id: string) =>
    api.get<IssuedToken[]>(`/users/${encodeURIComponent(id)}/tokens`, { schema: z.array(IssuedTokenSchema) }),
  revokeToken: (id: string, tokenId: string) =>
    api.del<RevokeResult>(`/users/${encodeURIComponent(id)}/tokens/${encodeURIComponent(tokenId)}`, {
      schema: RevokeResultSchema,
    }),
  revokeAllTokens: (id: string) =>
    api.post<RevokeResult>(`/users/${encodeURIComponent(id)}/revoke-tokens`, undefined, {
      schema: RevokeResultSchema,
    }),
  resetMfa: (id: string) => api.post<void>(`/users/${encodeURIComponent(id)}/reset-mfa`),
  resetCredentials: (id: string, password?: string) =>
    api.post<{ ok: boolean; generated: boolean; password: string }>(
      `/users/${encodeURIComponent(id)}/reset-credentials`,
      password ? { password } : {},
    ),
  bindings: (id: string) => api.get<Binding[]>(`/users/${encodeURIComponent(id)}/bindings`, { schema: z.array(BindingSchema) }),
  createBinding: (id: string, data: { namespace?: string; roleName: string; roleKind?: string; scope?: 'namespaced' | 'cluster' }) =>
    api.post<Binding>(`/users/${encodeURIComponent(id)}/bindings`, data),
  deleteBinding: (id: string, bindingId: string) =>
    api.del<void>(`/users/${encodeURIComponent(id)}/bindings/${encodeURIComponent(bindingId)}`),
};

export const namespacesApi = {
  list: () =>
    api.get<Paginated<Namespace> | Namespace[]>('/namespaces', {
      schema: z.union([PaginatedSchema(NamespaceSchema), z.array(NamespaceSchema)]),
    }),
};

export const rolesApi = {
  list: () => api.get<Role[]>('/roles'),
};

export type DashboardStats = {
  users: { total: number; active: number; disabled: number };
  audit: { total: number; windowDays: number; windowTotal: number; histogram: { day: string; count: number }[] };
};

export const dashboardApi = {
  // Real aggregate counts — the users/audit list endpoints are capped and carry
  // no totals, so KPIs must come from here.
  stats: (days: number) => api.get<DashboardStats>('/dashboard/stats', { params: { days }, schema: DashboardStatsSchema }),
};

export const clusterApi = {
  // ClusterRoles the operator has explicitly allow-listed for cluster-wide
  // grants (empty when cluster-wide provisioning is disabled).
  bindableRoles: () => api.get<string[]>('/cluster-bindable-roles'),
  // ClusterRoles allowed as targets of a namespaced RoleBinding (view/edit/admin…).
  namespacedBindableRoles: () => api.get<string[]>('/bindable-roles'),
};

export const auditApi = {
  list: (params: AuditListParams = {}) =>
    api.get<Paginated<AuditEvent>>('/audit', {
      params: params as Record<string, string | number | boolean | undefined>,
      schema: z.union([PaginatedSchema(AuditEventSchema), z.array(AuditEventSchema)]),
    }),
};

export const settingsApi = {
  get: () => api.get<AppSettings>('/settings', { schema: AppSettingsSchema }),
  update: (data: Partial<AppSettings>) => api.put<AppSettings>('/settings', data, { schema: AppSettingsSchema }),
};

export const idpApi = {
  get: () => api.get<IdpConfig>('/admin/idp'),
  update: (data: Partial<IdpConfig>) => api.put<IdpConfig>('/admin/idp', data),
  test: (data?: Partial<IdpConfig>) => api.post<IdpTestResult>('/admin/idp/test', data ?? {}),
  mappings: {
    list: () => api.get<GroupRoleMapping[]>('/admin/idp/mappings'),
    create: (m: GroupRoleMapping) => api.post<GroupRoleMapping>('/admin/idp/mappings', m),
    update: (id: string, m: GroupRoleMapping) =>
      api.put<GroupRoleMapping>(`/admin/idp/mappings/${encodeURIComponent(id)}`, m),
    remove: (id: string) => api.del<void>(`/admin/idp/mappings/${encodeURIComponent(id)}`),
  },
};
